"use client";

/**
 * Explicit-content age gate — shared 18+ confirmation.
 *
 * Explicit tracks are NOT hidden; they're visible everywhere. But you can't
 * PLAY them (gated in the media player) or READ their lyrics until you confirm
 * you're 18+. The single localStorage flag `bb-explicit-ok` is shared with the
 * player, so confirming once unlocks both. Confirmation broadcasts a
 * `bb:explicit-ok` event so every mounted gate updates instantly in this tab.
 */

import React, { useCallback, useEffect, useState } from "react";
import { ParentalAdvisoryLabel } from "@/components/ui/parental-advisory-label";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";

const KEY = "bb-explicit-ok";
const EVENT = "bb:explicit-ok";

export function confirmExplicitOk() {
  try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(EVENT));
}

export function useExplicitOk(): [boolean, () => void] {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    const read = () => {
      try { setOk(localStorage.getItem(KEY) === "1"); } catch { setOk(false); }
    };
    read();
    window.addEventListener(EVENT, read);
    window.addEventListener("storage", read); // other tabs
    return () => {
      window.removeEventListener(EVENT, read);
      window.removeEventListener("storage", read);
    };
  }, []);
  return [ok, confirmExplicitOk];
}

/**
 * Wrap any content that must be gated for explicit tracks. When `explicit` is
 * true and the viewer hasn't confirmed 18+, the children are blurred and a
 * frosted unlock card is shown on top.
 */
export function ExplicitGate({
  explicit,
  children,
  label = "This track is marked explicit.",
  action = "I'm 18+ — show it",
}: {
  explicit?: boolean;
  children: React.ReactNode;
  label?: string;
  action?: string;
}) {
  const [ok, confirm] = useExplicitOk();
  if (!explicit || ok) return <>{children}</>;

  return (
    <div className="relative">
      <div className="pointer-events-none select-none blur-md" aria-hidden>
        {children}
      </div>
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-xl bg-background/70 p-6 text-center backdrop-blur-sm">
        <ParentalAdvisoryLabel width={96} className="mx-auto" />
        <p className="max-w-xs text-sm text-muted-foreground">
          {label} Confirm you&apos;re 18 or older to continue.
        </p>
        <Button onClick={confirm} className="gap-1.5">
          <Lock className="h-4 w-4" /> {action}
        </Button>
      </div>
    </div>
  );
}
